import { Accordion } from "@/components/ui/library/accordion";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import SideBarAccordanItem from "./SideBarAccordanItem";
import type { SidebarSection } from "../../useSidebarSections";
import { useLeadFormLockedFields } from "@/features/leads/api/useLeadFormConfig";

const normalizePath = (path: string) =>
  `/${String(path ?? "").replace(/^\/+/, "").replace(/\/+$/, "")}`;

/**
 * STAGES and FAVORITE VIEWS are an exclusive pair — at most ONE of the two is
 * open, FAVORITE VIEWS by default, or STAGES when the workspace picked it in
 * Settings → Manage → "Sidebar opens on". Every other section (MODULE,
 * CONFIGURATIONS, and every HR section) is pinned permanently open. Matched by
 * the server's group key (tabs.services.ts GROUP_LABEL_MAP), label as fallback.
 *
 * `sections` is the ACTIVE suite's list (see useSidebarSections) — the CRM's
 * server-driven groups, or HR's own manifest. Both render through here so the
 * two suites cannot drift apart visually.
 */
const EXCLUSIVE_GROUPS = new Set(["core", "favorite"]);
/**
 * A branch pipeline's stage section (server group `pipeline-<id>`) is part of
 * the STAGES family: the family opens and closes together, and is exclusive
 * with FAVORITE VIEWS exactly like STAGES alone is.
 */
const isBranchPipeline = (section: any) =>
  String(section?.group ?? "").startsWith("pipeline-");
const isExclusive = (section: any) =>
  EXCLUSIVE_GROUPS.has(section?.group) ||
  isBranchPipeline(section) ||
  section?.label === "STAGES" ||
  section?.label === "FAVORITE VIEWS";
const isFavorites = (section: any) =>
  section?.group === "favorite" || section?.label === "FAVORITE VIEWS";
const isStages = (section: any) =>
  section?.group === "core" || section?.label === "STAGES";
const isStageFamily = (section: any) => isStages(section) || isBranchPipeline(section);

export default function SideBarAccordan({
  sections,
}: {
  sections: SidebarSection[];
}) {
  const { pathname } = useLocation();

  // The section that owns the route we're currently on — that's the one that
  // stays open. Longest match wins so a sub-route (`/leads/archived`) resolves
  // to the section owning `/leads/archived`, not the one owning `/leads`.
  const activeSection = useMemo(() => {
    const current = normalizePath(pathname);
    let match: { label: string; length: number } | null = null;

    for (const section of sections) {
      for (const route of section.routes ?? []) {
        const routePath = normalizePath(route.path);
        if (routePath === "/") continue;
        if (current !== routePath && !current.startsWith(`${routePath}/`))
          continue;
        if (!match || routePath.length > match.length)
          match = { label: section.label, length: routePath.length };
      }
    }

    return match?.label;
  }, [sections, pathname]);

  const exclusiveLabels = useMemo(
    () => sections.filter(isExclusive).map((s) => s.label),
    [sections]
  );
  // The workspace's "Sidebar opens on" pick. Until it has loaded nothing is
  // seeded, so a Stages workspace never flashes Favorite Views first.
  const { sidebarDefaultOpen, isLoading: prefLoading } =
    useLeadFormLockedFields();
  const defaultOpen = useMemo(() => {
    const preferred = sections.find(
      sidebarDefaultOpen === "stages" ? isStages : isFavorites,
    );
    return preferred?.label ?? exclusiveLabels[0] ?? "";
  }, [sections, sidebarDefaultOpen, exclusiveLabels]);

  const [openSection, setOpenSection] = useState<string>("");
  // STAGES and the branch pipeline sections read as one: any of them open
  // means all of them are. No branch pipelines → only STAGES is in the family.
  const stageFamilyOpen = useMemo(
    () => sections.some((s) => isStageFamily(s) && s.label === openSection),
    [sections, openSection],
  );
  // The default last seeded — if that is still what's open when the setting
  // changes, the sidebar follows the new pick; a section someone opened by
  // hand stays open.
  const seeded = useRef("");

  // Navigating into STAGES or FAVORITE VIEWS opens that one (closing the
  // other). Routes outside the pair — Dashboard, WhatsApp, and the pinned
  // MODULE / CONFIGURATIONS sections — fall back to the default without
  // clobbering a choice the user already made.
  useEffect(() => {
    if (activeSection && exclusiveLabels.includes(activeSection)) {
      setOpenSection(activeSection);
    } else if (!prefLoading) {
      const previous = seeded.current;
      setOpenSection((cur) => (!cur || cur === previous ? defaultOpen : cur));
    }
    if (!prefLoading) seeded.current = defaultOpen;
  }, [activeSection, defaultOpen, exclusiveLabels, prefLoading]);

  return (
    <div className="w-full">
      {sections.map((section, i) =>
        isExclusive(section) ? (
          <Accordion
            key={section.label || section.group || i}
            type="single"
            collapsible
            value={
              openSection === section.label ||
              (stageFamilyOpen && isStageFamily(section))
                ? section.label
                : ""
            }
            onValueChange={(value) =>
              setOpenSection(value ? section.label : "")
            }
            className="w-full"
          >
            <SideBarAccordanItem section={section} />
          </Accordion>
        ) : (
          // Pinned sections get their own controlled root with a fixed value,
          // so they render open and can never close. A headerless section
          // (HR's primary group) renders plainly inside it — see the item.
          <Accordion
            key={section.label || section.group || i}
            type="multiple"
            value={[section.label]}
            className="w-full"
          >
            <SideBarAccordanItem section={section} alwaysOpen />
          </Accordion>
        )
      )}
    </div>
  );
}
